import { useEffect, useRef } from "react";
import { useStore } from "../store/store";
import type { ConversationView } from "../adapters/clients";

/** Follows a conversation while the page shows it: long-polls the DO's events and refreshes the view, the meters and the balances. */
export function useLiveConversation(id: string | null | undefined): ConversationView | undefined {
  const { runner, signedIn, conversations, setConversation, refreshAuth, refreshBalances } = useStore();
  const view = conversations.find((c) => c.id === id);
  const seqRef = useRef(0);
  useEffect(() => {
    if (!id || !runner || !signedIn) return;
    const ctl = new AbortController();
    let stopped = false;
    seqRef.current = 0;
    void (async () => {
      try {
        const fresh = await runner.get(id);
        setConversation(fresh);
        seqRef.current = fresh.eventSeq;
        await refreshAuth(id, fresh.agent);
      } catch {
        /* not on this runner */
        return;
      }
      while (!stopped) {
        try {
          const r = await runner.events(id, seqRef.current, 20000, ctl.signal);
          if (stopped) return;
          seqRef.current = Math.max(seqRef.current, r.seq);
          if (r.events.length > 0) {
            const v = await runner.get(id);
            setConversation(v);
            const types = new Set(r.events.map((e) => e.type));
            if (
              types.has("settled") ||
              types.has("answer") ||
              types.has("paused") ||
              types.has("done") ||
              types.has("card")
            ) {
              await refreshAuth(id, v.agent).catch(() => undefined);
              await refreshBalances().catch(() => undefined);
            }
          }
        } catch (e) {
          if (stopped || (e instanceof DOMException && e.name === "AbortError")) return;
          await new Promise((r) => setTimeout(r, 2000));
        }
      }
    })();
    return () => {
      stopped = true;
      ctl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, runner, signedIn]);
  return view;
}

export function useMedia(query: string): boolean {
  const { current } = useRef(typeof window !== "undefined" ? window.matchMedia(query) : null);
  const [matches, set] = useStateSafe(current?.matches ?? false);
  useEffect(() => {
    if (!current) return;
    const on = () => set(current.matches);
    current.addEventListener("change", on);
    return () => current.removeEventListener("change", on);
  }, [current, set]);
  return matches;
}

import { useState } from "react";
function useStateSafe<T>(v: T) {
  return useState(v);
}
